import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Button } from '@mui/material';
import { Input } from '../../components/ui/input';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

export default function ImportInvoiceItemsDialog({ open, onClose, onImport, products }) {
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [data, setData] = useState([]);
  const [mapping, setMapping] = useState({
    code: '',
    qty: '',
    price: ''
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });
      
      if (jsonData.length > 0) {
        setHeaders(jsonData[0]); // First row as headers
        setData(jsonData.slice(1)); // Rest as data
        
        // Auto-guess mapping
        const guess = { code: '', qty: '', price: '' };
        jsonData[0].forEach(h => {
          const lower = String(h).toLowerCase();
          if (lower.includes('code') || lower.includes('barcode') || lower.includes('رمز') || lower.includes('باركود')) guess.code = h;
          if (lower.includes('qty') || lower.includes('quantity') || lower.includes('كمية') || lower.includes('عدد')) guess.qty = h;
          if (lower.includes('price') || lower.includes('cost') || lower.includes('سعر')) guess.price = h;
        });
        setMapping(guess);
      }
    };
    reader.readAsBinaryString(selectedFile);
  };

  const handleImport = () => {
    if (!mapping.code || !mapping.qty) {
      toast.warning('يرجى تحديد عمود الرمز والكمية على الأقل');
      return;
    }

    const codeIndex = headers.indexOf(mapping.code);
    const qtyIndex = headers.indexOf(mapping.qty);
    const priceIndex = mapping.price ? headers.indexOf(mapping.price) : -1;

    const importedLines = [];
    let notFoundCount = 0;

    data.forEach(row => {
      const rawCode = row[codeIndex];
      if (!rawCode) return;

      const codeStr = String(rawCode).trim();
      
      // Lookup product by ID, Barcode, or even Name if needed (sticking to Code/Barcode as requested)
      // Assuming 'products' has 'id', 'barcode', 'sku', etc.
      // The user said "by symbol (code) or barcode".
      // Let's check typical product structure. usually: id, barcode, code?
      // In Invoicesales.jsx: p.barcode, p.id. Let's check if there is a 'code' field.
      // Usually 'sku' or 'itemCode'. I'll assume barcode or exact ID match or search in fields.
      
      let product = products.find(p => 
        (p.code && String(p.code) === codeStr) || // ✅ Search by Item Code first
        (p.barcode && String(p.barcode) === codeStr) ||
        (p.itemCode && String(p.itemCode) === codeStr) || 
        (String(p.id) === codeStr)
      );
      
      // Fallback: search in units barcodes
      if (!product) {
         for (const p of products) {
            if (p.units && Array.isArray(p.units)) {
                if (p.units.some(u => String(u.barcode) === codeStr)) {
                    product = p;
                    break;
                }
            }
         }
      }

      if (product) {
        const qty = parseFloat(row[qtyIndex]) || 1;
        const price = priceIndex > -1 ? parseFloat(row[priceIndex]) : null; // null means use system price

        importedLines.push({
          product,
          qty,
          price
        });
      } else {
        notFoundCount++;
      }
    });

    if (importedLines.length === 0) {
      toast.error('لم يتم العثور على أي أصناف مطابقة');
      return;
    }

    onImport(importedLines);
    if (notFoundCount > 0) {
      toast.warning(`تم استيراد ${importedLines.length} صنف. لم يتم العثور على ${notFoundCount} صنف.`);
    } else {
      toast.success(`تم استيراد ${importedLines.length} صنف بنجاح`);
    }
    onClose();
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Item Code", "Quantity", "Price"],
      ["1001", "10", "100"],
      ["BARCODE123", "5", "50"]
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "invoice_items_template.xlsx");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle className="font-bold text-lg flex justify-between items-center">
        <span>استيراد أصناف من Excel</span>
        <Button variant="text" size="small" onClick={handleDownloadTemplate} className="text-blue-600 text-sm">
          📥 تحميل قالب تجريبي
        </Button>
      </DialogTitle>
      <DialogContent className="space-y-4 pt-4">
        
        {/* File Input */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
          <input 
            type="file" 
            accept=".xlsx, .xls, .csv" 
            onChange={handleFileChange} 
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
          />
        </div>

        {/* Mapping Section */}
        {headers.length > 0 && (
          <div className="grid grid-cols-1 gap-4 bg-white p-4 rounded border">
            <h4 className="font-bold border-b pb-2">تعيين الأعمدة</h4>
            
            <div>
              <label className="block text-sm font-bold mb-1">رمز الصنف / الباركود <span className="text-red-500">*</span></label>
              <select 
                value={mapping.code} 
                onChange={(e) => setMapping({...mapping, code: e.target.value})}
                className="w-full border rounded p-2 bg-white"
              >
                <option value="">-- اختر العمود --</option>
                {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">الكمية <span className="text-red-500">*</span></label>
              <select 
                value={mapping.qty} 
                onChange={(e) => setMapping({...mapping, qty: e.target.value})}
                className="w-full border rounded p-2 bg-white"
              >
                <option value="">-- اختر العمود --</option>
                {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">السعر (اختياري)</label>
              <select 
                value={mapping.price} 
                onChange={(e) => setMapping({...mapping, price: e.target.value})}
                className="w-full border rounded p-2 bg-white"
              >
                <option value="">-- افتراضي (من البطاقة) --</option>
                {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
              </select>
            </div>
            
            <div className="text-sm text-gray-500 mt-2">
              عدد الصفوف المكتشفة: {data.length}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outlined" onClick={onClose} className="border-gray-300 text-gray-700">إلغاء</Button>
          <Button variant="contained" onClick={handleImport} disabled={!file || !mapping.code || !mapping.qty} className="bg-blue-600 text-white hover:bg-blue-700">
            استيراد
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
}
